import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import io from "socket.io-client";
import axios from "axios";
import {
  API_URL,
  SOCKET_URL,
  SOCKET_PATH,
  MAX_CART_ITEMS,
} from "../config/constants";

export const PENDING_ORDER_KEY = "barrista_pending_order_id";

const OrderContext = createContext();
const OrderProgressContext = createContext();

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrder must be used within OrderProvider");
  }
  return context;
};

// Separate context for actionProgress - Dashboard doesn't use it, so won't re-render when cooking progress updates
export const useOrderProgress = () => {
  const context = useContext(OrderProgressContext);
  if (!context) {
    throw new Error("useOrderProgress must be used within OrderProvider");
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionProgress, setActionProgress] = useState(null); // { orderId, itemId, stepIndex, totalSteps, progressPercent, message }
  const [orderHasFailedItem, setOrderHasFailedItem] = useState(false);
  const activeOrderRef = useRef(null);
  const actionProgressThrottleRef = useRef({
    last: 0,
    pending: null,
    timer: null,
  });

  // Update ref when activeOrder changes
  useEffect(() => {
    activeOrderRef.current = activeOrder;
  }, [activeOrder]);

  // Per-device order tracking: use sessionStorage for "my" pending order (multi-user QR scenario)
  // Returns true if there is an active order (pending/processing/cooking), false if done or none
  const checkActiveOrder = useCallback(async () => {
    try {
      const storedOrderId = sessionStorage.getItem(PENDING_ORDER_KEY);
      if (storedOrderId) {
        const orderId = parseInt(storedOrderId, 10);
        if (Number.isFinite(orderId)) {
          const response = await axios.post(
            `${API_URL}/graphql`,
            {
              query: `
                query($orderId: Int!) {
                  order(order_id: $orderId) {
                    order_id
                    order_number
                    order_status
                    total_amount
                    kiosk_type
                    items {
                      item_id
                      recipe_id
                      quantity
                      status
                      recipe {
                        recipe_name
                      }
                    }
                  }
                }
              `,
              variables: { orderId },
            },
            { timeout: 12000 },
          );
          const order = response.data?.data?.order;
          const status = (order?.order_status || "").toLowerCase();
          if (order && ["pending", "processing", "cooking"].includes(status)) {
            setActiveOrder(order);
            setIsProcessing(true);
            const hasFailed = order.items?.some((i) => i.status === "failed");
            setOrderHasFailedItem(!!hasFailed);
            return true;
          }
          if (order && ["ready", "completed", "cancelled"].includes(status)) {
            sessionStorage.removeItem(PENDING_ORDER_KEY);
            setActiveOrder(null);
            setIsProcessing(false);
            setOrderHasFailedItem(false);
            return false;
          }
        } else {
          sessionStorage.removeItem(PENDING_ORDER_KEY);
          setActiveOrder(null);
          setIsProcessing(false);
          return false;
        }
      }
      return false;
    } catch (error) {
      // Silent - network error, will retry on next mount or user action
      return false;
    }
  }, []);

  useEffect(() => {
    // Initialize Socket.IO connection with polling fallback
    const newSocket = io(SOCKET_URL, {
      path: SOCKET_PATH,
      transports: ["polling", "websocket"], // Try polling first, then upgrade to websocket
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
      timeout: 30000,
      forceNew: true,
    });

    newSocket.on("connect", () => {
      console.log("Connected to server");
    });

    const handleOrderStatusUpdate = (data) => {
      const currentOrder = activeOrderRef.current;
      const storedOrderId = sessionStorage.getItem(PENDING_ORDER_KEY);
      const isMyOrder =
        (currentOrder && data.orderId === currentOrder.order_id) ||
        (storedOrderId && String(data.orderId) === storedOrderId);
      if (isMyOrder) {
        const status = (data.status || "").toLowerCase();
        setActiveOrder((prev) =>
          prev
            ? { ...prev, order_status: status }
            : { order_id: data.orderId, order_status: status },
        );
        if (["ready", "completed", "cancelled"].includes(status)) {
          sessionStorage.removeItem(PENDING_ORDER_KEY);
          setIsProcessing(false);
          setActionProgress(null);
          setOrderHasFailedItem(false);
        } else if (["processing", "cooking"].includes(status)) {
          setIsProcessing(true);
        }
      }
    };

    const ACTION_PROGRESS_THROTTLE_MS = 250;

    const handleActionProgress = (data) => {
      const currentOrder = activeOrderRef.current;
      if (!currentOrder || data.orderId !== currentOrder.order_id) return;

      const payload = {
        orderId: data.orderId,
        itemId: data.itemId,
        stepIndex: data.stepIndex,
        totalSteps: data.totalSteps,
        progressPercent: data.progressPercent,
        message: data.message,
        step: data.step,
      };
      const now = Date.now();
      const throttle = actionProgressThrottleRef.current;
      if (now - throttle.last >= ACTION_PROGRESS_THROTTLE_MS) {
        throttle.last = now;
        throttle.pending = null;
        setActionProgress(payload);
      } else {
        throttle.pending = payload;
        if (!throttle.timer) {
          throttle.timer = setTimeout(
            () => {
              throttle.timer = null;
              throttle.last = Date.now();
              if (throttle.pending) {
                setActionProgress(throttle.pending);
                throttle.pending = null;
              }
            },
            ACTION_PROGRESS_THROTTLE_MS - (now - throttle.last),
          );
        }
      }
    };

    const handleOrderItemStatusUpdate = (data) => {
      const currentOrder = activeOrderRef.current;
      const storedOrderId = sessionStorage.getItem(PENDING_ORDER_KEY);
      const isMyOrder =
        (currentOrder && data.orderId === currentOrder.order_id) ||
        (storedOrderId && String(data.orderId) === storedOrderId);
      if (isMyOrder && data.status === "failed") {
        setOrderHasFailedItem(true);
      }
    };

    newSocket.on("orderStatusUpdate", handleOrderStatusUpdate);
    newSocket.on("orchestratorActionUpdate", handleActionProgress);
    newSocket.on("orderItemStatusUpdate", handleOrderItemStatusUpdate);

    setSocket(newSocket);

    // Check for active order on mount
    checkActiveOrder();

    return () => {
      const t = actionProgressThrottleRef.current.timer;
      if (t) clearTimeout(t);
      newSocket.off("orderStatusUpdate", handleOrderStatusUpdate);
      newSocket.off("orchestratorActionUpdate", handleActionProgress);
      newSocket.close();
    };
  }, [checkActiveOrder]);

  const addToCartWithQuantity = useCallback((recipe, quantity) => {
    if (quantity <= 0) return;
    setCart((prev) => {
      const existingItem = prev.find(
        (item) => item.recipe_id === recipe.recipe_id,
      );
      const totalItems = prev.reduce((sum, item) => sum + item.quantity, 0);
      const currentQty = existingItem ? existingItem.quantity : 0;
      const newQty = existingItem ? currentQty + quantity : quantity;
      const totalAfter = totalItems - currentQty + newQty;

      if (totalAfter > MAX_CART_ITEMS) return prev;

      if (existingItem) {
        return prev.map((item) =>
          item.recipe_id === recipe.recipe_id
            ? { ...item, quantity: newQty }
            : item,
        );
      }
      return [...prev, { ...recipe, quantity }];
    });
  }, []);

  const addToCart = useCallback(
    (recipe) => addToCartWithQuantity(recipe, 1),
    [addToCartWithQuantity],
  );

  const removeFromCart = useCallback((recipeId) => {
    setCart((prev) => prev.filter((item) => item.recipe_id !== recipeId));
  }, []);

  const updateQuantity = useCallback(
    (recipeId, quantity) => {
      if (quantity <= 0) {
        removeFromCart(recipeId);
        return;
      }

      setCart((prev) => {
        // Calculate total items with new quantity
        const totalItems = prev.reduce((sum, item) => {
          if (item.recipe_id === recipeId) {
            return sum + quantity;
          }
          return sum + item.quantity;
        }, 0);

        // Check total items limit
        if (totalItems > MAX_CART_ITEMS) {
          return prev; // Max total items reached
        }

        return prev.map((item) =>
          item.recipe_id === recipeId ? { ...item, quantity } : item,
        );
      });
    },
    [removeFromCart],
  );

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cart.reduce(
      (total, item) => total + item.recipe_price * item.quantity,
      0,
    );
  }, [cart]);

  const getCartItemCount = useCallback(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  // Core value - NO actionProgress so Dashboard won't re-render on cooking progress updates
  const coreValue = useMemo(
    () => ({
      cart,
      activeOrder,
      isProcessing,
      orderHasFailedItem,
      socket,
      addToCart,
      addToCartWithQuantity,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount,
      setActiveOrder,
      setIsProcessing,
      checkActiveOrder,
    }),
    [
      cart,
      activeOrder,
      isProcessing,
      orderHasFailedItem,
      socket,
      addToCart,
      addToCartWithQuantity,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount,
      checkActiveOrder,
    ],
  );

  const progressValue = useMemo(
    () => ({ actionProgress, setActionProgress }),
    [actionProgress],
  );

  return (
    <OrderContext.Provider value={coreValue}>
      <OrderProgressContext.Provider value={progressValue}>
        {children}
      </OrderProgressContext.Provider>
    </OrderContext.Provider>
  );
};
